import {
    type CreateCloudQueueOptions,
    type CreateQueueResult,
    type FetchRemoteQueueResult,
} from '@/cloudQueue/cloudQueueService'
import {
    createLogPrefix,
    ErrorLevel,
    first,
    logDebug,
    logError,
    logVerbose,
    ReportableError,
    type LogTarget,
} from '@amazon/vinyl-util'
import {
    type EntityPlaybackCapabilities,
    type ManagedPlaylistController,
    type PlaylistEntry,
    type QueueCapabilities,
} from '@/playback/PlaylistController'
import type { PlayableEntity } from '@/service/fragments/playableEntityFrag'
import { playableEntityToPlaylistEntry } from '@/playback/playableEntityToPlaylistEntry'
import { isExplicitEntry } from '@/playback/skipGuard'
import {
    isExplicitLanguageFilterOn,
    toastExplicitBlocked,
} from '@/explicitLanguageFilter/explicitLanguageFilterService'
import { ctx } from '@/util/context'
import { catalogizeLibraryAsinMrn } from '@/util/mrn'
import { cancelPlaybackMeasure, markPlayStart } from '@/perf/playbackDelayMarks'
import type { Player } from '@/playback/api'
import type { FeedProvider } from '@/util/async/FeedProvider'
import { toast } from '@/components/toast'
import { getStringAsync } from '@/playback/i18n/bundle.generated'

const { target, errorHandler } = ctx('setCloudQueue')

export interface CloudQueueControllerDeps {
    readonly playlistController: ManagedPlaylistController
    readonly playerPromise: Promise<Player>
}

export interface SetCloudQueueOptions extends CreateCloudQueueOptions {
    readonly queueResult: CreateQueueResult

    /**
     * The entity the caller actually asked to play. Firefly's
     * `createQueue` anchors the returned window's `current` on the
     * container's default track and can omit the requested track from
     * the window entirely — when that happens the window search below
     * finds nothing and would otherwise fall back to the container's
     * first entry (the classic "clicked track 5, played track 1"
     * regression). Seeding on this entity keeps the clicked track
     * playing even when the gateway drops it from the window.
     */
    readonly requestedEntity?: PlayableEntity | null

    /**
     * Set when an end-of-queue autoplay continuation installs this queue,
     * not a user tap. Suppresses the initial-playback-delay measure — IPD
     * tracks user-initiated plays only.
     */
    readonly autoContinuation?: boolean
}

export interface CloudQueueController {
    /**
     * Sets a feed-provider on the playlist controller backed by this
     * cloud queue. Steps the controller forward onto the queue's
     * current entry and starts playback.
     */
    setQueue(options: SetCloudQueueOptions): void

    /**
     * Hop the existing cloud queue onto a specific entry — e.g. when
     * the user clicks a track in the now-playing queue rail. We
     * preserve the same queue (and therefore the same parent
     * container, autoplay context, etc.) and just shift the cursor.
     *
     * The entry's `entityReferenceId` must come from the same
     * CloudQueue that's currently feeding the playlist controller.
     * If the controller isn't backed by this cloud-queue feed (or
     * the entry has no ref) the call is a no-op.
     */
    seekToEntry(entry: PlaylistEntry): void

    /**
     * Re-pull the live cloud queue window from the gateway and seed
     * the feed's cursor map from the fresh edges. Used after a
     * mutation that changes the server-side ordering or membership —
     * shuffle, loop, reorder, remove — so the next walk reflects the
     * new order. Mirrors hornet's
     * `refetchLocalQueue` (which fans out to `fetchRemoteQueue` +
     * `_loadNewLocalWindowIntoManager`). No-op if no queue is live.
     */
    refetchQueue(): Promise<void>

    /**
     * Adopt a page-reload restore feed (built off a fresh FetchRemoteQueue
     * window) as the live queue and park the playlist controller on its
     * current entry PAUSED. No-op when something is already playing — a
     * queue the user started during boot must not be clobbered. The feed
     * build + fetch live in the lazy `queueRestore` module so they stay off
     * the boot chunk; this method just installs the result.
     */
    installRestoredFeed(feed: CloudQueueFeed, positionSec: number): Promise<void>
}

export class CloudQueueControllerImpl implements CloudQueueController, LogTarget {
    logPrefix = createLogPrefix('CloudQueueControllerImpl')

    /**
     * Live cloud-queue feed. Held so seekToEntry can re-prime the
     * feed's entityReferenceId cursor with the clicked row's ref
     * before asking the playlist controller to hop onto it.
     */
    private liveFeed: CloudQueueFeed | null = null

    constructor(private readonly deps: CloudQueueControllerDeps) {}

    setQueue(options: SetCloudQueueOptions): void {
        logDebug(this, 'setCloudQueue', options)
        const { queueResult } = options
        const { playlistController, playerPromise } = this.deps
        const feed = createCloudQueueFeed(queueResult, options)
        logVerbose(
            this,
            `[preload-trace] setQueue: seedEntry=${feed.seedEntry?.id ?? 'null'} autoContinuation=${!!options.autoContinuation}`
        )
        // Universal play sink — every fresh user play funnels through here, so
        // opening the initial-playback-delay window here covers detail-page
        // heroes and track-list rows that bypass blackbolt/playback.ts. Must fire
        // synchronously, ahead of the engine's mediaLoad stage, or that stage is
        // dropped. No label: the lane is named at `playing` after the actual
        // now-playing track, not the seed. Skipped for an autoplay continuation,
        // which re-seeds without a user tap.
        const measureToken = options.autoContinuation ? undefined : markPlayStart()
        this.liveFeed = feed
        playlistController.setDataProvider(feed)
        // Close the perf window opened above if the play never reaches audio (no
        // entry, or a rejected step/play) so a later unrelated `playing` can't
        // draw a stale bar. Token-guarded: only OUR window — autoplay never
        // opened one, and a newer play that overwrote the owner survives.
        const abandonMeasure = () => {
            if (!options.autoContinuation) cancelPlaybackMeasure('setQueue-abandon', measureToken)
        }
        playlistController
            .skipNext()
            .then(async (entry) => {
                if (!entry) {
                    abandonMeasure()
                    return
                }
                const player = await playerPromise
                player.play().catch((error) => {
                    abandonMeasure()
                    errorHandler(error)
                })
            })
            .catch((error) => {
                abandonMeasure()
                errorHandler(error)
            })
    }

    seekToEntry(entry: PlaylistEntry): void {
        const ref = entry.entityReferenceId
        if (!ref || !this.liveFeed) return
        // The server queue view still lists explicit rows; refuse to jump onto
        // one under an active filter so the drawer can't bypass the feed's skip.
        if (isFilteredByExplicit(entry)) {
            toastExplicitBlocked()
            return
        }
        // Make sure the feed knows this entry's ref so subsequent
        // next()/previous() walks resolve from here.
        this.liveFeed.registerEntryRef(entry, ref)
        this.deps.playlistController.seekToEntry(entry)
    }

    async refetchQueue(): Promise<void> {
        const feed = this.liveFeed
        if (!feed) return
        const queueId = feed.cloudQueueOptions.queueId
        if (!queueId) return
        const currentRef = this.deps.playlistController.current.value?.entityReferenceId ?? null
        logDebug(this, 'refetchQueue', { queueId, currentRef })
        try {
            const { fetchRemoteQueue } = await import('@/cloudQueue/cloudQueueService')
            const result = await fetchRemoteQueue(queueId, currentRef)
            feed.seedFromWindow(result)
            // Now that the feed's cursor map is rebuilt, ask the
            // playlist controller to re-resolve its prefetched `next`.
            this.deps.playlistController.invalidateNext()
        } catch (error) {
            logError(this, 'refetchQueue: window refetch failed', error)
            toast(getStringAsync('dmusic_queue_update_failed_toast'))
            // SILENT: the toast above IS the user-facing handling — callers
            // still see the rejection for their own state-revert logic, but
            // errorHandler's debug toast shouldn't stack a second one.
            throw new ReportableError('queue window refetch failed', undefined, ErrorLevel.SILENT)
        }
    }

    async installRestoredFeed(feed: CloudQueueFeed, positionSec: number): Promise<void> {
        const { playlistController, playerPromise } = this.deps
        // A function (not a narrowed read) so the re-check after the await
        // isn't flow-narrowed to "always false" — the observable can change
        // under us (the user starts playing) while we await the player.
        const alreadyQueued = () => playlistController.current.value != null
        logVerbose(
            this,
            `[preload-trace] installRestoredFeed: seedEntry=${feed.seedEntry?.id ?? 'null'} alreadyQueued=${alreadyQueued()} positionSec=${positionSec}`
        )
        if (alreadyQueued()) return
        this.liveFeed = feed
        // Ensure the playback-side `current` subscriptions (feed-paused load,
        // concurrency, cursor report) are installed before we park `current`.
        await playerPromise
        if (alreadyQueued()) return
        await playlistController.restoreProvider(feed)
        // Restore the play position on the user's first play (the paused
        // player drains this the moment it starts — same seam as `?t=` links).
        if (positionSec > 0) playlistController.pendingSeek(positionSec)
    }
}

export type CloudQueueFeed = ReturnType<typeof createCloudQueueFeed>

/**
 * A single queue edge as returned by the `queueEntityConnection`
 * fragment — the seam where we read the raw per-entity capability
 * selection before projecting it into the gql-free
 * `EntityPlaybackCapabilities` the transport consumes.
 */
type QueueEdge = NonNullable<
    NonNullable<NonNullable<CreateQueueResult['createQueue']>['queue']>['current']
>['edges'][number]

/**
 * Project a queue edge's raw `QueueEntityCapability` selection into
 * the transport-facing `EntityPlaybackCapabilities`. Absence (no
 * capabilities block, or an individual action the gateway omits)
 * means permissive — `isAllowed` is only false when the gateway says
 * so, mirroring the queue-level capability defaulting.
 */
function toEntityCapabilities(caps: QueueEdge['capabilities']): EntityPlaybackCapabilities | null {
    if (!caps) return null
    const skips = caps.next?.metadata?.remainingSkips
    const upsell = caps.next?.metadata?.upsell
    return {
        nextAllowed: caps.next?.isAllowed !== false,
        previousAllowed: caps.previous?.isAllowed !== false,
        scrubForwardAllowed: caps.scrubForward?.isAllowed !== false,
        scrubBackwardAllowed: caps.scrubBackward?.isAllowed !== false,
        loopOneAllowed: caps.loopOne?.isAllowed !== false,
        ...(skips != null ? { remainingSkips: skips } : null),
        ...(upsell != null ? { upsell } : null),
    }
}

/**
 * Whether the explicit-language filter is active AND this entry is explicit.
 * Such entries are omitted from playback — never seeded, and skipped over when
 * walking the queue. Read live so a mid-session toggle takes effect on the next
 * walk (the OFF→ON toggle also clears the live queue outright).
 */
function isFilteredByExplicit(entry: PlaylistEntry | null): boolean {
    return isExplicitLanguageFilterOn() && isExplicitEntry(entry)
}

/**
 * Feed-provider over a Firefly CloudQueue. Maintains a parallel map
 * from PlaylistEntry → entityReferenceId so the controller can hand
 * us an entry and we know which cursor to walk from. The current
 * track from `queueResult` seeds the map; subsequent next/previous
 * batches add to it as they're fetched.
 */
export function createCloudQueueFeed(
    queueResult: CreateQueueResult,
    options: CreateCloudQueueOptions & { readonly requestedEntity?: PlayableEntity | null }
): FeedProvider<PlaylistEntry> & {
    readonly cloudQueueOptions: CreateCloudQueueOptions
    readonly queueCapabilities: QueueCapabilities
    readonly registerEntryRef: (entry: PlaylistEntry, ref: string) => void
    readonly seedFromWindow: (result: FetchRemoteQueueResult) => void
    readonly seedEntry: PlaylistEntry | null
} {
    const queue = queueResult.createQueue?.queue
    const queueId = queue?.id ?? null
    // Stash the live queue id on cloudQueueOptions so observers
    // (e.g. NowPlayingQueueView) can read the cloud queue's id
    // without us threading another observable through.
    const cloudQueueOptionsWithId: CreateCloudQueueOptions = { ...options, queueId }
    // Mirror hornet's queueCapabilities surfacing — gateway returns
    // a per-queue allow / deny for shuffle / loopAll. Default to
    // allowed when the field is missing so existing queues keep
    // working; deny only when the gateway explicitly says so.
    const caps = queue?.capabilities
    const queueCapabilities: QueueCapabilities = {
        shuffleAllowed: caps?.shuffle?.isAllowed ?? true,
        loopAllAllowed: caps?.loopAll?.isAllowed ?? true,
        blockQueueMutations: caps?.blockQueueMutations?.isAllowed === true,
    }

    // entry-id → entityReferenceId. We key by PlaylistEntry.id rather
    // than the object identity so the same logical entry can survive
    // a re-fetch (e.g. invalidateCache + re-peek) and still resolve.
    const refByEntryId = new Map<string, string>()

    function stampRef(
        entry: PlaylistEntry,
        ref: string | null,
        caps?: QueueEdge['capabilities'],
        metricId?: string | null
    ): PlaylistEntry {
        const capabilities = caps ? toEntityCapabilities(caps) : null
        return {
            ...entry,
            ...(ref ? { entityReferenceId: ref } : null),
            ...(capabilities ? { capabilities } : null),
            ...(metricId ? { metricId } : null),
        }
    }

    // Project a walked edge into a stamped entry, registering its ref so
    // later walks resolve from here. Null for an empty edge or an
    // unsupported node type.
    function edgeToStampedEntry(edge: QueueEdge | null | undefined): PlaylistEntry | null {
        if (!edge?.node) return null
        const rawEntry = playableEntityToPlaylistEntry(edge.node as PlayableEntity)
        if (!rawEntry) {
            logError(target, 'Unsupported track type:', edge.node.__typename)
            return null
        }
        if (edge.entityReferenceId) refByEntryId.set(rawEntry.id, edge.entityReferenceId)
        return stampRef(rawEntry, edge.entityReferenceId ?? null, edge.capabilities, edge.metricId)
    }

    // Walk `current + next` and register every edge's entityReferenceId
    // so subsequent seek / walk calls can resolve their cursor. We must
    // register more than the seed row: Firefly's `createQueue` anchors
    // `current` on the container's FIRST item ignoring the requested
    // playParams.id (see comment on the CreateQueue mutation), so the
    // entry we actually want to seed on lives in `next(limit:)`.
    const windowEdges = [...(queue?.current?.edges ?? []), ...(queue?.next?.edges ?? [])]
    const windowEntries: PlaylistEntry[] = []
    for (const edge of windowEdges) {
        const raw = playableEntityToPlaylistEntry(edge.node as PlayableEntity)
        if (!raw) continue
        const stamped = stampRef(
            raw,
            edge.entityReferenceId ?? null,
            edge.capabilities,
            edge.metricId
        )
        windowEntries.push(stamped)
        if (edge.entityReferenceId) refByEntryId.set(stamped.id, edge.entityReferenceId)
    }

    // WORKAROUND (mirrors hornet's CreateLocalQueueWindow.ts): the
    // requested playParams.id can be a track deep in the container,
    // but Firefly's createQueue always anchors `current` on the
    // container's first item. Search the returned window for the
    // requested id (via playParams.id or uri) and seed on that entry
    // if found.
    // Match the catalog-backed id the mutation actually sent (see
    // createCloudQueue) so a library-scoped requested track still resolves
    // against the catalog edges the gateway returns.
    const requestedId = options.playParams.id
        ? catalogizeLibraryAsinMrn(options.playParams.id)
        : options.playParams.id
    const foundInWindow = windowEntries.find(
        (e) =>
            !isFilteredByExplicit(e) &&
            (e.entity.playParams?.id === requestedId ||
                e.entity.uri === requestedId ||
                e.id === requestedId)
    )

    // When the requested track isn't in the returned window at all —
    // Firefly can hand back a short window anchored on the container's
    // default track that omits the one we asked for — seed on the
    // caller-supplied entity so the CLICKED track plays. Without this
    // the fallback to `windowEntries[0]` plays the container's first
    // track regardless of which row was clicked. Its entityReferenceId
    // is unknown (it wasn't in the window), so subsequent next/previous
    // walks resolve once the gateway re-anchors on play.
    const requestedEntry =
        options.requestedEntity && playableEntityToPlaylistEntry(options.requestedEntity)
    // A requested track masked by the active filter must not seed playback.
    const seedableRequested =
        requestedEntry && !isFilteredByExplicit(requestedEntry) ? requestedEntry : null

    // First window entry the explicit-language filter leaves playable — the
    // container play-all seed, and the fallback when the requested track is
    // absent or itself filtered.
    const firstPlayable = windowEntries.find((e) => !isFilteredByExplicit(e)) ?? null

    const seedEntry: PlaylistEntry | null = foundInWindow ?? seedableRequested ?? firstPlayable

    // The window is a bounded view, not the whole container: when every window
    // entry is masked under an active filter, seed the walk on the last window
    // edge so `next` skips PAST the boundary to a playable track further down
    // before treating the container as all-explicit. The all-masked toast then
    // fires only if that forward walk also finds nothing.
    const maskedWindowFallback: PlaylistEntry | null =
        !seedEntry && windowEntries.length > 0 && isExplicitLanguageFilterOn()
            ? (windowEntries[windowEntries.length - 1] ?? null)
            : null

    /**
     * Holds the resolved seed entry for the controller's first
     * `next(null)` call. Cleared once consumed so subsequent calls
     * walk via getNextCloudQueueItems.
     */
    let pendingSeed: PlaylistEntry | null = seedEntry ?? maskedWindowFallback

    /**
     * Pre-resolved `next(currentEntry)` answer, populated by
     * `seedFromWindow` after a queue refetch (shuffle / loop /
     * reorder / remove). Maps
     * the parked entry's id → the freshly-walked next entry the
     * gateway returned in the same FetchRemoteQueue window. Consumed
     * once on the very next `next()` call so we don't issue a
     * follow-up `NextTrackInQueue` (which would hit Firefly's
     * api-cache and return the pre-mutation answer).
     */
    const pendingNextByEntryId = new Map<string, PlaylistEntry>()

    async function walkNext(current: PlaylistEntry | null): Promise<PlaylistEntry | null> {
        if (current == null) {
            // Seed the controller with the queue's current entry. If
            // there's no seed (empty queue), fall through to the
            // gateway with a null reference, which yields the head.
            if (pendingSeed) {
                const seed = pendingSeed
                pendingSeed = null
                return seed
            }
        }
        if (current && pendingNextByEntryId.has(current.id)) {
            const cached = pendingNextByEntryId.get(current.id)!
            pendingNextByEntryId.delete(current.id)
            return cached
        }
        if (!queueId) return null
        const fromRef = current ? (refByEntryId.get(current.id) ?? null) : null
        if (!fromRef) return null
        try {
            const { getNextCloudQueueItems } = await import('@/cloudQueue/cloudQueueService')
            const result = await getNextCloudQueueItems(queueId, fromRef)
            return edgeToStampedEntry(first(result.queue?.next?.edges))
        } catch (e) {
            logError(target, e)
            return null
        }
    }

    // Advance past explicit entries so the filter omits them from playback.
    // Bounded by a visited-cursor set: under loopOne/loopAll the gateway
    // re-serves entries forever, so a repeated cursor means an all-explicit
    // cycle — bail to null instead of spinning a never-resolving chain.
    async function next(current: PlaylistEntry | null): Promise<PlaylistEntry | null> {
        const seedingPastMaskedWindow =
            current == null && maskedWindowFallback != null && pendingSeed === maskedWindowFallback
        let entry = await walkNext(current)
        const visited = new Set<string>()
        while (entry && isFilteredByExplicit(entry)) {
            const cursor = entry.entityReferenceId ?? entry.id
            if (visited.has(cursor)) {
                entry = null
                break
            }
            visited.add(cursor)
            entry = await walkNext(entry)
        }
        if (seedingPastMaskedWindow && !entry) toastExplicitBlocked()
        return entry
    }

    async function walkPrevious(current: PlaylistEntry): Promise<PlaylistEntry | null> {
        if (!queueId) return null
        const fromRef = refByEntryId.get(current.id) ?? null
        if (!fromRef) return null
        try {
            const { getPreviousCloudQueueItems } = await import('@/cloudQueue/cloudQueueService')
            const result = await getPreviousCloudQueueItems(queueId, fromRef)
            const edges = result.queue?.previous?.edges
            // The previous batch comes back oldest-first; the
            // immediately-previous entry sits at the end of the array.
            const edge = edges && edges.length > 0 ? edges[edges.length - 1] : null
            return edgeToStampedEntry(edge)
        } catch (e) {
            logError(target, e)
            return null
        }
    }

    // Step back past explicit entries, mirroring `next` — same visited-cursor
    // bound so an all-explicit loop terminates instead of recursing forever.
    async function previous(current: PlaylistEntry): Promise<PlaylistEntry | null> {
        let entry = await walkPrevious(current)
        const visited = new Set<string>()
        while (entry && isFilteredByExplicit(entry)) {
            const cursor = entry.entityReferenceId ?? entry.id
            if (visited.has(cursor)) return null
            visited.add(cursor)
            entry = await walkPrevious(entry)
        }
        return entry
    }

    /**
     * Lets external surfaces (e.g. NowPlayingQueueView's click-to-play)
     * pre-prime the cursor with an entry whose ref the feed hasn't
     * walked to yet. Without this, calling
     * playlistController.seekToEntry on a row from `view` (which the
     * feed only knows about by ref, not by entry-id) would leave
     * subsequent next() calls returning null.
     */
    function registerEntryRef(entry: PlaylistEntry, ref: string): void {
        if (entry.id) refByEntryId.set(entry.id, ref)
    }

    /**
     * Re-seed the feed from a fresh queue window — used after a
     * server-side mutation (shuffle / loop / reorder / remove) so
     * subsequent `next()` / `previous()` walks reflect the new order.
     *
     * Mirrors hornet's `_loadNewLocalWindowIntoManager`: rebuild the
     * entry-id → entityReferenceId map from every edge in the
     * window, and pre-cache the walked-once next answer so the
     * controller's follow-up `invalidateNext()` doesn't have to
     * re-issue `NextTrackInQueue` (which would otherwise hit
     * Firefly's api-cache and return the pre-mutation answer).
     */
    function seedFromWindow(result: FetchRemoteQueueResult): void {
        const fresh = result.queue
        if (!fresh) return

        // Walk the current edge → register its ref. The PlaylistEntry
        // we're parked on still matches by id (server doesn't
        // re-mint the entity), so re-registering its ref against
        // potentially-shifted entityReferenceId is the correct hop.
        const currentEdge = first(fresh.current?.edges)
        const currentRef = currentEdge?.entityReferenceId ?? null
        const currentEntry =
            currentEdge?.node && playableEntityToPlaylistEntry(currentEdge.node as PlayableEntity)
        if (currentEntry && currentRef) {
            refByEntryId.set(currentEntry.id, currentRef)
        }

        // Cache the new next-entry against the parked entry's id so
        // the controller's invalidateNext() resolves to it without
        // a cached-server roundtrip.
        const nextEdge = first(fresh.next?.edges)
        const nextRef = nextEdge?.entityReferenceId ?? null
        const nextEntry =
            nextEdge?.node && playableEntityToPlaylistEntry(nextEdge.node as PlayableEntity)
        if (currentEntry && nextEntry) {
            const stamped = stampRef(nextEntry, nextRef, nextEdge.capabilities, nextEdge.metricId)
            pendingNextByEntryId.set(currentEntry.id, stamped)
            if (nextRef) refByEntryId.set(nextEntry.id, nextRef)
        }

        // Register every previous edge's ref too — keeps skipPrevious
        // working immediately after the refetch.
        for (const edge of fresh.previous?.edges ?? []) {
            if (!edge.entityReferenceId) continue
            const entry = playableEntityToPlaylistEntry(edge.node as PlayableEntity)
            if (entry) refByEntryId.set(entry.id, edge.entityReferenceId)
        }
    }

    return {
        next,
        previous,
        registerEntryRef,
        seedFromWindow,
        seedEntry,
        cloudQueueOptions: cloudQueueOptionsWithId,
        queueCapabilities,
    }
}
